export const CONSENT_STORAGE_KEY = 'oogst_cookie_consent';
export const CONSENT_CHANGED_EVENT = 'oogst:consent-changed';
export const ATTRIBUTION_STORAGE_KEY = 'oogst_attribution';

const GTM_ID = 'GTM-KTFKMX4L';
const GTM_SRC = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;

const CONSENT_FIELDS = {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
};

const EVENT_PARAMETERS = {
    sign_up: ['event_id', 'method', 'market'],
    activation: ['event_id', 'activation_type'],
    begin_checkout: ['event_id', 'currency', 'value', 'plan_id'],
    purchase: ['event_id', 'transaction_id', 'currency', 'value', 'items'],
    refund: ['event_id', 'transaction_id', 'currency', 'value'],
    cancel_subscription: ['event_id'],
};

const ATTRIBUTION_FIELDS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'fbclid',
];

const ATTRIBUTION_ENUMS = {
    utm_source: new Set(['affiliate', 'bing', 'direct', 'facebook', 'google', 'instagram', 'meta', 'newsletter', 'partner', 'seo']),
    utm_medium: new Set(['affiliate', 'blog', 'cpc', 'display', 'email', 'organic', 'paid_social', 'referral', 'social']),
};

const SAFE_CAMPAIGN_SLUG = /^[a-z0-9]+(?:[-_][a-z0-9]+)+$/i;
const CAMPAIGN_TOKENS = new Set(['ad', 'affiliate', 'brand', 'campaign', 'content', 'creative', 'display', 'eu', 'fall', 'keyword', 'launch', 'newsletter', 'nl', 'paid', 'partner', 'prospecting', 'retargeting', 'search', 'social', 'spring', 'summer', 'us', 'winter']);
const SAFE_CLICK_ID = /^(?=.{20,100}$)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z0-9_-]+$/;
const SAFE_NAMED_CAMPAIGNS = new Set(['blog-post']);

const isOpaqueId = value => (
    typeof value === 'string'
    && (/^\d{1,10}$/.test(value)
        || /^[a-f0-9]{8}-[a-f0-9-]{27,}$/.test(value)
        || /^\d{13}-[a-f0-9]+$/.test(value))
);
const isSafeEventId = (eventName, value) => (
    typeof value === 'string'
    && value.startsWith(`${eventName}:`)
    && isOpaqueId(value.slice(eventName.length + 1))
);
const isFiniteAmount = value => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const hasOnlyKeys = (value, keys) => (
    value && typeof value === 'object' && !Array.isArray(value)
    && Object.keys(value).every(key => keys.includes(key))
);

const isSafePurchaseItems = items => (
    Array.isArray(items)
    && items.length === 1
    && hasOnlyKeys(items[0], ['item_id', 'item_name'])
    && items[0].item_id === 'oogst-app'
    && ['Oogst.app subscription', 'Plucking subscription'].includes(items[0].item_name)
);

const EVENT_VALIDATORS = {
    sign_up: event => (
        isSafeEventId('sign_up', event.event_id)
        && event.method === 'email'
        && (event.market === undefined || ['EU', 'NL', 'US'].includes(event.market))
    ),
    activation: event => isSafeEventId('activation', event.event_id) && event.activation_type === 'first_planting',
    begin_checkout: event => (
        isSafeEventId('begin_checkout', event.event_id)
        && ['EUR', 'USD'].includes(event.currency)
        && isFiniteAmount(event.value)
        && ((typeof event.plan_id === 'number' && Number.isSafeInteger(event.plan_id) && event.plan_id >= 0)
            || (typeof event.plan_id === 'string' && /^\d{1,10}$/.test(event.plan_id)))
    ),
    purchase: event => (
        isSafeEventId('purchase', event.event_id)
        && isOpaqueId(event.transaction_id)
        && ['EUR', 'USD'].includes(event.currency)
        && isFiniteAmount(event.value)
        && (event.items === undefined || isSafePurchaseItems(event.items))
    ),
    refund: event => (
        isSafeEventId('refund', event.event_id)
        && isOpaqueId(event.transaction_id)
        && ['EUR', 'USD'].includes(event.currency)
        && isFiniteAmount(event.value)
    ),
    cancel_subscription: event => isSafeEventId('cancel_subscription', event.event_id),
};

const readStorage = (storage, key) => {
    try {
        return storage.getItem(key);
    } catch {
        return null;
    }
};

const writeStorage = (storage, key, value) => {
    try {
        storage.setItem(key, value);
        return true;
    } catch {
        return false;
    }
};

export const getStoredConsent = () => {
    const value = readStorage(window.localStorage, CONSENT_STORAGE_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
};

export function initializeConsentMode() {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() {
        window.dataLayer.push(arguments);
    };
    if (window.__oogstConsentModeInitialized) return;

    window.__oogstConsentModeInitialized = true;
    window.gtag('consent', 'default', {...CONSENT_FIELDS, wait_for_update: 500});
    window.gtag('set', 'ads_data_redaction', true);
    window.gtag('set', 'url_passthrough', true);
}

function loadGtm() {
    if (document.querySelector(`script[data-consent-script][src="${GTM_SRC}"]`)) return;

    window.dataLayer.push({'gtm.start': Date.now(), event: 'gtm.js'});
    const script = document.createElement('script');
    script.async = true;
    script.src = GTM_SRC;
    script.dataset.consentScript = 'true';
    document.head.appendChild(script);
}

export function applyTrackingConsent(value) {
    try {
        if (value !== 'granted' && value !== 'denied') return false;
        writeStorage(window.localStorage, CONSENT_STORAGE_KEY, value);
        initializeConsentMode();
        const state = value === 'granted' ? 'granted' : 'denied';
        window.gtag('consent', 'update', Object.fromEntries(
            Object.keys(CONSENT_FIELDS).map(field => [field, state])
        ));
        if (value === 'granted') loadGtm();
        if (value === 'granted') flushPendingActivations();
        window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, {detail: {consent: value}}));
        return true;
    } catch {
        return false;
    }
}

const randomEventSeed = () => (
    window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`
);

export function createFunnelEventId(eventName, seed = randomEventSeed()) {
    const safeEvent = String(eventName || '').replace(/[^a-z0-9_:-]/gi, '').slice(0, 40);
    const safeSeed = String(seed || '').replace(/[^a-z0-9_:-]/gi, '').slice(0, 80);
    return safeEvent && safeSeed ? `${safeEvent}:${safeSeed}` : null;
}

export function getOrCreateFunnelEventId(eventName) {
    try {
        const key = `oogst_event_id:${eventName}`;
        const existing = window.sessionStorage.getItem(key);
        if (existing) return existing;
        const eventId = createFunnelEventId(eventName);
        window.sessionStorage.setItem(key, eventId);
        return eventId;
    } catch {
        return createFunnelEventId(eventName, Date.now());
    }
}

export function clearFunnelEventId(eventName) {
    try {
        window.sessionStorage.removeItem(`oogst_event_id:${eventName}`);
    } catch {
        // A completed event does not depend on storage availability.
    }
}

export function getAnalyticsScopeFromToken(token) {
    try {
        const payload = token?.split('.')[1];
        if (!payload) return null;
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const parsed = JSON.parse(atob(base64));
        return parsed.id === undefined || parsed.id === null ? null : String(parsed.id);
    } catch {
        return null;
    }
}

export function trackFunnelEvent(eventName, parameters = {}) {
    try {
        const allowed = EVENT_PARAMETERS[eventName];
        if (!allowed || getStoredConsent() !== 'granted') return false;
        if (!hasOnlyKeys(parameters, allowed)) return false;
        initializeConsentMode();
        const event = {event: eventName};
        allowed.forEach(key => {
            if (parameters[key] !== undefined && parameters[key] !== null) {
                event[key] = parameters[key];
            }
        });
        if (!EVENT_VALIDATORS[eventName]?.(event)) return false;
        window.dataLayer.push(event);
        return true;
    } catch {
        return false;
    }
}

export function trackActivation(plantingId, accountScope = 'browser') {
    try {
        const safeScope = String(accountScope || 'browser').replace(/[^a-z0-9_-]/gi, '').slice(0, 80) || 'browser';
        const storageKey = `oogst_activation:${safeScope}`;
        let state = JSON.parse(readStorage(window.localStorage, storageKey) || 'null');
        if (state?.sent) return false;
        if (!state?.event_id) {
            state = {event_id: createFunnelEventId('activation', plantingId), sent: false};
            writeStorage(window.localStorage, storageKey, JSON.stringify(state));
        }
        const tracked = trackFunnelEvent('activation', {
            event_id: state.event_id,
            activation_type: 'first_planting',
        });
        if (tracked) writeStorage(window.localStorage, storageKey, JSON.stringify({...state, sent: true}));
        return tracked;
    } catch {
        return false;
    }
}

function flushPendingActivations() {
    try {
        for (let index = 0; index < window.localStorage.length; index += 1) {
            const key = window.localStorage.key(index);
            if (!key?.startsWith('oogst_activation:')) continue;
            const state = JSON.parse(readStorage(window.localStorage, key) || 'null');
            if (!state?.event_id || state.sent) continue;
            const tracked = trackFunnelEvent('activation', {
                event_id: state.event_id,
                activation_type: 'first_planting',
            });
            if (tracked) writeStorage(window.localStorage, key, JSON.stringify({...state, sent: true}));
        }
    } catch {
        // Consent updates must remain best-effort when storage is unavailable.
    }
}

let activationTrackingQueue = Promise.resolve();

export function queueActivationTracking(plantingPromise, accountScope) {
    activationTrackingQueue = activationTrackingQueue
        .then(() => plantingPromise)
        .then(createdPlanting => trackActivation(createdPlanting?.id, accountScope))
        .catch(() => false);
    return plantingPromise;
}

function sanitizeAttributionValue(field, value) {
    if (typeof value !== 'string') return null;
    const normalized = value.trim().slice(0, 100);
    if (!normalized || /@|\s/.test(normalized)) return null;
    if (ATTRIBUTION_ENUMS[field]) {
        const canonical = normalized.toLowerCase();
        return ATTRIBUTION_ENUMS[field].has(canonical) ? canonical : null;
    }
    if (field === 'gclid' || field === 'fbclid') {
        return SAFE_CLICK_ID.test(normalized) ? normalized : null;
    }
    if (['utm_campaign', 'utm_term', 'utm_content'].includes(field)) {
        if (field === 'utm_campaign' && SAFE_NAMED_CAMPAIGNS.has(normalized.toLowerCase())) {
            return normalized.toLowerCase();
        }
        const tokens = normalized.toLowerCase().split(/[-_]/);
        return SAFE_CAMPAIGN_SLUG.test(normalized) && tokens.some(token => /^\d{1,10}$/.test(token))
            && tokens.every(token => CAMPAIGN_TOKENS.has(token) || /^\d{1,10}$/.test(token))
            ? normalized
            : null;
    }
    return null;
}

const sanitizeAttributionTouch = touch => {
    if (!touch || typeof touch !== 'object') return null;
    const sanitized = {};
    for (const field of ATTRIBUTION_FIELDS) {
        if (touch[field] === undefined || touch[field] === null) continue;
        if (
            field === 'utm_campaign'
            && sanitized.utm_source === 'affiliate'
            && sanitized.utm_medium === 'affiliate'
            && typeof touch[field] === 'string'
            && /^\d{1,10}$/.test(touch[field])
        ) {
            sanitized[field] = touch[field];
            continue;
        }
        const value = sanitizeAttributionValue(field, touch[field]);
        if (!value) return null;
        sanitized[field] = value;
    }
    if (!Object.keys(sanitized).length) return null;
    if (Number.isFinite(touch.timestamp)) sanitized.timestamp = touch.timestamp;
    return sanitized;
};

export function getStoredAttribution() {
    try {
        const parsed = JSON.parse(readStorage(window.localStorage, ATTRIBUTION_STORAGE_KEY));
        const firstTouch = sanitizeAttributionTouch(parsed?.first_touch);
        const lastTouch = sanitizeAttributionTouch(parsed?.last_touch);
        if (!firstTouch || !lastTouch) {
            window.localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
            return null;
        }
        const sanitized = {first_touch: firstTouch, last_touch: lastTouch};
        writeStorage(window.localStorage, ATTRIBUTION_STORAGE_KEY, JSON.stringify(sanitized));
        return sanitized;
    } catch {
        return null;
    }
}

export function sanitizeRegistrationAttribution(value) {
    if (!value || typeof value !== 'object') return null;
    const timestamp = Number(value.timestamp);
    if (!Number.isFinite(timestamp)) return null;
    const utmSource = sanitizeAttributionValue('utm_source', value.utm_source);
    const utmMedium = sanitizeAttributionValue('utm_medium', value.utm_medium);
    const affiliateCampaign = utmSource === 'affiliate' && utmMedium === 'affiliate'
        && typeof value.utm_campaign === 'string' && /^\d{1,10}$/.test(value.utm_campaign)
        ? value.utm_campaign
        : null;
    return {
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: affiliateCampaign || sanitizeAttributionValue('utm_campaign', value.utm_campaign),
        supplier_ids: typeof value.supplier_ids === 'string' && /^\d+(,\d+)*$/.test(value.supplier_ids)
            ? value.supplier_ids
            : null,
        timestamp,
    };
}

export function captureAttribution(search = window.location.search) {
    try {
        const params = new URLSearchParams(search);
        const touch = {};
        ATTRIBUTION_FIELDS.forEach(field => {
            const value = sanitizeAttributionValue(field, params.get(field));
            if (value) touch[field] = value;
        });
        if (
            touch.utm_source === 'affiliate'
            && touch.utm_medium === 'affiliate'
            && /^\d{1,10}$/.test(params.get('utm_campaign') || '')
        ) {
            touch.utm_campaign = params.get('utm_campaign');
        }
        if (!Object.keys(touch).length) return getStoredAttribution();

        touch.timestamp = Date.now();
        const current = getStoredAttribution();
        const attribution = {
            first_touch: current?.first_touch || touch,
            last_touch: touch,
        };
        writeStorage(window.localStorage, ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
        return attribution;
    } catch {
        return getStoredAttribution();
    }
}
